import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { CalendarEvent } from '@/lib/absences';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { absenceFillStyle } from '@/lib/absence-chip-style';
import { EVENT_TYPE_FALLBACK_COLOR } from '@/lib/calendar-event-colors';
import { barTopForLane } from './schedule-lanes';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/date';
import { eachDayOfInterval, format, isWeekend, startOfDay } from 'date-fns';

interface ScheduleBarProps {
  event: CalendarEvent;
  spanDays: number;
  isPending?: boolean;
  /** Vertical lane for stacking overlapping bars (HW-701). Defaults to 0. */
  lane?: number;
}

export function ScheduleBar({ event, spanDays, isPending, lane = 0 }: ScheduleBarProps) {
  const formatName = useEmployeeNameFormatter();
  // Working-day count, not calendar days. Dariia (HR): "при подсчете оплаты
  // обращаем внимание именно на рабочие дни, а так придется высчитывать
  // каждого". Weekends are dropped; public holidays aren't tracked in the
  // model so they aren't subtracted (follow-up if HR asks).
  const workingDays = eachDayOfInterval({
    start: startOfDay(parseDateOnly(event.startsOn)),
    end: startOfDay(parseDateOnly(event.endsOn)),
  }).filter((d) => !isWeekend(d)).length;
  // HW-719: per-type brand fallback (shared with month grid + exports), not gray,
  // so a null-colour type matches on screen and in the export.
  const color = event.absenceTypeColor || EVENT_TYPE_FALLBACK_COLOR[event.type];
  const fill = absenceFillStyle(color, Boolean(isPending));

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'absolute z-[3] flex h-[26px] cursor-pointer items-center overflow-hidden rounded-md px-2 text-[11px] font-medium transition-[filter] hover:brightness-[1.2] hover:z-[4]',
              // HW-701: approved = solid fill + white text; pending = outline
              // only (transparent fill, colored border + colored text).
              fill.className
            )}
            style={{
              left: '2px',
              top: `${barTopForLane(lane)}px`,
              width: `calc(${spanDays * 100}% - 4px)`,
              ...fill.style,
            }}
          >
            <span className="truncate">
              {spanDays >= 3 ? (event.absenceTypeName || event.type) : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] space-y-1">
          <p className="font-semibold">{event.absenceTypeName || event.type}</p>
          <p className="text-xs opacity-80">
            {formatName({
              firstName: event.employeeFirstName ?? '',
              lastName: event.employeeLastName ?? '',
              preferredName: event.employeePreferredName,
            })}
          </p>
          <p className="text-xs opacity-80">
            {format(parseDateOnly(event.startsOn), 'MMM d')} — {format(parseDateOnly(event.endsOn), 'MMM d')}{' '}
            ({workingDays} working day{workingDays !== 1 ? 's' : ''})
          </p>
          {isPending && (
            <span className="mt-0.5 inline-block rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-400">
              Pending
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
